import React, { useState } from "react";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { Box, Stack, Typography } from "@mui/material";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { IFolder } from "store/types";

const DIVIDER = "1px solid #E5E8EF";
const INDENT = 32;

interface Props {
  folders: IFolder[];
  folder: IFolder;
  selectedFolderId: number | null;
  onSelectFolder: (folder: IFolder) => void;
  searchQuery?: string;
  level?: number;
}

const folderMatchesSearch = (folder: IFolder, folders: IFolder[], query: string): boolean => {
  if (!query) return true;
  const lower = query.toLowerCase();
  if (folder.title.toLowerCase().includes(lower)) return true;
  return folders.filter((f) => f.parent_id === folder.id).some((child) => folderMatchesSearch(child, folders, lower));
};

export const Folder: React.FC<Props> = ({ folders, folder, selectedFolderId, onSelectFolder, searchQuery = "", level = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const childrenFolders = folders.filter((f) => f.parent_id === folder.id);
  const isSelected = selectedFolderId === folder.id;
  const hasChildren = Boolean(childrenFolders.length);
  const isFiltering = Boolean(searchQuery);
  const shouldExpand = expanded || isFiltering;

  const visibleChildren = isFiltering
    ? childrenFolders.filter((child) => folderMatchesSearch(child, folders, searchQuery))
    : childrenFolders;

  if (isFiltering && !folder.title.toLowerCase().includes(searchQuery.toLowerCase()) && visibleChildren.length === 0) {
    return null;
  }

  return (
    <>
      <Stack
        data-folder
        direction="row"
        alignItems="center"
        sx={{
          borderBottom: DIVIDER,
          cursor: "pointer",
          userSelect: "none",
          height: 32,
          backgroundColor: isSelected ? "#EDF4FB" : "transparent",
          "&:hover": { backgroundColor: isSelected ? "#EDF4FB" : "#F5F7FA" }
        }}
        onClick={() => onSelectFolder(folder)}
      >
        <Box sx={{ width: INDENT + level * INDENT, flexShrink: 0 }} />
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: "6px", minWidth: 0 }}>
          <ExpandMoreIcon
            onClick={(e) => {
              e.stopPropagation();
              if (hasChildren) setExpanded(!expanded);
            }}
            sx={{
              fontSize: 20,
              color: "#071F35",
              transition: "transform 0.15s",
              transform: shouldExpand ? "none" : "rotate(-90deg)",
              visibility: hasChildren ? "visible" : "hidden",
              cursor: hasChildren ? "pointer" : "default"
            }}
          />
          <FolderOutlinedIcon sx={{ fontSize: 16, color: "#071F35" }} />
          <MaterialTooltip variant="dark" title={folder.title} placement="top" isShow={folder.title.length <= 35}>
            <Typography noWrap sx={{ fontSize: 14, color: "#071F35" }}>
              {folder.title}
            </Typography>
          </MaterialTooltip>
        </Box>
      </Stack>
      {shouldExpand &&
        visibleChildren.map((childFolder) => (
          <Folder
            key={childFolder.id}
            folders={folders}
            folder={childFolder}
            selectedFolderId={selectedFolderId}
            onSelectFolder={onSelectFolder}
            searchQuery={searchQuery}
            level={level + 1}
          />
        ))}
    </>
  );
};
